<!--
 * @FileDescription: 角色管理页面
 * @Author: 陈文景
 * @Date: 2022-09-22 14:32:37
 * @LastEditors: 最后更新作者
 * @LastEditTime: 最后更新时间
-->
<template>
  <page-skeleton>
    <template slot="header-left">
      <el-button size="medium" type="primary" round @click="addRole">新增角色</el-button>
      <el-input v-model="parameter.name" clearable size="medium" placeholder="搜索">
        <el-button slot="append" icon="el-icon-search" @click="getSystemRolesList" />
      </el-input>
    </template>

    <template slot="container">
      <el-table v-loading="loading" :data="roles" size="medium" row-key="id" :style="{ width: `${tableWidth}%` }" :height="`${tableHeight}%`">
        <el-table-column prop="name" label="角色名称" />
        <el-table-column prop="description" label="描述" />
        <el-table-column prop="type" label="角色类型">
          <template v-slot="{ row }">
            {{ roleType(row.type) }}
          </template>
        </el-table-column>
        <el-table-column label="创建时间">
          <template v-slot="{ row }">
            {{ formatTimeStamp(row.createDate) }}
          </template>
        </el-table-column>
        <el-table-column prop="creater" label="创建人" />
        <el-table-column prop="modifier" label="修改人" />
        <el-table-column label="最后编辑时间">
          <template v-slot="{ row }">
            {{ formatTimeStamp(row.modifyDate) }}
          </template>
        </el-table-column>
        <el-table-column prop="permissionCount" label="权限数量" />
        <el-table-column prop="userCount" label="成员数量" />
        <el-table-column label="状态">
          <template v-slot="{ row }">
            <span v-if="row.status === 1" class="enable">正常</span>
            <span v-else-if="row.status === 0" class="disable">停用</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column fixed="right" label="操作" width="120">
          <template v-slot="{ row }">
            <el-button type="text" size="medium" @click="() => handleMembersManagement(row)">成员管理</el-button>
            <el-button type="text" size="medium" @click="() => editRole(row)">编辑</el-button>
          </template>
        </el-table-column>
      </el-table>
    </template>

    <template #footer-right>
      <el-pagination background :current-page="parameter.pageNumber" :page-sizes="[20, 50, 100, 200, 500]" :page-size="parameter.pageSize" layout="total, sizes, prev, pager, next, jumper" :total="total" class="el-pagination" @size-change="sizeChange" @current-change="currentChange" />
    </template>

    <!--新增/编辑角色对话框 -->
    <add-or-edit-dialog ref="addOrEditDialog" :title="title" @update="getSystemRolesList" />

    <!-- 成员管理对话框 -->
    <member-manage-dialog ref="memberManageDialog" @update="getSystemRolesList" />
  </page-skeleton>
</template>

<script>
import API from '@/api'
import { formatTimeStamp } from '@/utils/moment'
import PageSkeleton from '@/components/PageSkeleton.vue'
import AddOrEditDialog from './components/AddOrEditDialog.vue'
import MemberManageDialog from './components/MemberManageDialog.vue'

export default {
  name: 'user_roles',
  components: {
    'page-skeleton': PageSkeleton,
    'add-or-edit-dialog': AddOrEditDialog,
    'member-manage-dialog': MemberManageDialog
  },
  data() {
    return {
      tableWidth: 100,
      tableHeight: 100,

      formatTimeStamp,

      // 获取角色列表的请求参数
      parameter: {
        name: undefined, // 搜索关键字
        pageSize: 20,
        pageNumber: 1
      },
      roles: [], // 角色列表
      total: 0, // 角色列表总数量
      loading: false, // 表格loading

      title: '新增' // 新增或编辑对话框标题
    }
  },
  computed: {
    roleType() {
      return (type) => {
        switch (type) {
          case 1:
            return 'System'
          case 2:
            return '自定义角色'
          default:
            return '-'
        }
      }
    }
  },
  created() {
    this.getSystemRolesList()
  },
  methods: {
    /**
     * API 获取权限列表
     * @param {Boolean} reset 是否重置分页
     */
    async getSystemRolesList(reset = true) {
      try {
        if (reset) {
          this.parameter.pageNumber = 1
        }
        if (this.parameter.name){
          this.parameter.name = this.parameter.name.trim()
        }
        this.loading = true
        let { data } = await API.getSystemRolesList(this.parameter)
        this.total = data.total
        this.roles = data.list
        this.loading = false
      } catch (error) {
        this.loading = false
        throw new Error(error.message)
      }
    },

    /**
     * 新增角色
     */
    addRole() {
      this.title = '新增'
      this.$refs.addOrEditDialog.show()
    },

    /**
     * 编辑角色
     * @param {Object} row 当前行数据
     */
    editRole(row) {
      this.title = '编辑'
      this.$refs.addOrEditDialog.show(row)
    },

    /**
     * 成员管理
     * @param {Object} row 当前行数据
     */
    handleMembersManagement(row) {
      this.$refs.memberManageDialog.show(row)
    },

    /**
     * 每页显示条数发生改变时的回调
     * @param {Number} size 每页显示条数
     */
    sizeChange(size) {
      this.parameter.pageSize = size
      this.getSystemRolesList(false)
    },

    /**
     * 上一页/下一页/当前页发生改变时的回调
     * @param {Number} current 当前页
     */
    currentChange(current) {
      this.parameter.pageNumber = current
      this.getSystemRolesList(false)
    }
  }
}
</script>

<style lang="scss" scoped>
.el-input {
  width: 260px;
  /deep/.el-input__inner {
    border-top-left-radius: 18px;
    border-bottom-left-radius: 18px;
  }
  /deep/.el-input-group__append {
    border-top-right-radius: 18px !important;
    border-bottom-right-radius: 18px !important;
  }
}

.enable {
  color: #99c41c;
}

.disable {
  color: #f56c6c;
}
</style>
